import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorBox, Skeleton } from "../../components/ui";
import { useBuildNextYearCalendar, useEmployeeFiles, useTidyDepartments } from "../../lib/api";
import { SectionCard, Share, Stat } from "../labour/shared";

const SECTION_ORDER = ["identity", "contact", "emergency", "terms", "payment", "history"] as const;

/** Personnel files (WP-24 wave X, HRX-14): what the employee record holds, and what it does not.
 *
 * Every wave before this one computed something from the employee file — wave M a joining date, wave P a
 * contract type, wave Q a grade, wave U somebody to send a letter to. This one looks at the file itself.
 *
 * The screen ranks by what stops somebody being contacted, paid or accounted for, not by how many boxes are
 * filled. A file missing only a personal email address is not a file with a problem. */
export default function Files({ company }: { company: string }) {
  const { t } = useTranslation();
  const q = useEmployeeFiles(company || undefined);
  const calendar = useBuildNextYearCalendar();
  const tidy = useTidyDepartments();
  const [showAll, setShowAll] = useState(false);

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 grid gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  const d = q.data;
  const failing = d.state.filter((s) => !s.ok);
  const worst = [...d.employees].sort((a, b) => a.completeness.score - b.completeness.score);
  const shown = showAll ? worst : worst.slice(0, 15);
  const heaviest = Math.max(1, ...d.sections.map((s) => s.missing));

  return (
    <div className="mt-4 space-y-4" data-files>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-files-strip>
        <Stat label={t("efl.files")} value={d.summary.employees}
          hint={t("efl.filesHint", { n: d.summary.without_faults })} />
        <Stat label={t("efl.score")}
          value={d.summary.score === null ? "—" : `${Math.round(d.summary.score * 100)}%`}
          cls={d.summary.score !== null && d.summary.score < 0.8 ? "text-status-amber" : "text-status-green"}
          hint={t("efl.scoreHint")} />
        <Stat label={t("efl.unreachable")} value={d.summary.unreachable}
          cls={d.summary.unreachable ? "text-status-red" : "text-status-green"}
          hint={t("efl.unreachableHint")} />
        <Stat label={t("efl.noKin")} value={d.summary.no_emergency_contact}
          cls={d.summary.no_emergency_contact ? "text-status-red" : "text-status-green"}
          hint={t("efl.noKinHint")} />
        <Stat label={t("efl.unpayable")} value={d.summary.unpayable}
          cls={d.summary.unpayable ? "text-status-amber" : "text-status-green"}
          hint={t("efl.unpayableHint", { count: d.summary.bank_without_an_account })} />
      </div>

      {failing.length > 0 && (
        <SectionCard title={t("efl.findings")} aside={t("efl.findingsN", { n: failing.length, of: d.state.length })}>
          <ul>
            {failing.map((s) => (
              <li key={s.key} className="flex flex-wrap items-start justify-between gap-3 border-t border-brand-grey-light px-4 py-3 first:border-t-0"
                data-files-finding={s.key}>
                <div className="flex min-w-0 flex-1 items-start gap-2">
                  <span className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-status-amber" />
                  <div>
                    <div className="font-semibold">{t(`efl.check.${s.key}.title`)}</div>
                    <p className="mt-0.5 max-w-3xl text-sm text-brand-grey">{t(`efl.check.${s.key}.bad`, s.facts) as string}</p>
                  </div>
                </div>
                {d.permissions.manage && s.key === "calendar_reaches_ahead" && (
                  <button onClick={() => calendar.mutate({})} disabled={calendar.isPending}
                    data-files-calendar
                    className="shrink-0 rounded bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                    {t("efl.buildCalendar", { year: d.next_year })}
                  </button>
                )}
                {d.permissions.manage && s.key === "masters_are_usable" && d.departments.empty > 0 && (
                  <button onClick={() => tidy.mutate({ confirm: tidy.data?.confirm_required === false ? 1 : 0 })}
                    disabled={tidy.isPending} data-files-tidy
                    className="shrink-0 rounded border border-brand-primary px-3 py-1.5 text-xs font-semibold text-brand-primary disabled:opacity-50">
                    {tidy.data?.would_disable
                      ? t("efl.tidyConfirm", { n: tidy.data.would_disable })
                      : t("efl.tidy")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </SectionCard>
      )}

      <SectionCard title={t("efl.whereToStart")} aside={t("efl.sectionsHint")}>
        <table className="w-full text-sm">
          <tbody>
            {d.sections.map((s) => (
              <tr key={s.section} className="border-t border-brand-grey-light first:border-t-0"
                data-files-section={s.section}>
                <td className="w-44 px-4 py-2 font-medium">{t(`efl.section.${s.section}`)}</td>
                <td className="px-4 py-2"><Share value={s.missing} of={heaviest} /></td>
                <td className="w-40 px-4 py-2 text-right tabular-nums">
                  <span className={s.missing ? "font-semibold" : "text-brand-grey"}>
                    {t("efl.missingOf", { n: s.missing, of: s.of })}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="border-t border-brand-grey-light bg-[#F6F8FA] px-4 py-2 text-[11px] text-brand-grey">
          {t("efl.sectionsNote")}
        </p>
      </SectionCard>

      <SectionCard title={t("efl.thinnest")}
        aside={t("efl.thinnestHint", { shown: shown.length, of: d.employees.length })}>
        <table className="w-full text-sm">
          <tbody>
            {shown.map((e) => (
              <tr key={e.name} className="border-t border-brand-grey-light first:border-t-0"
                data-files-employee={e.name}>
                <td className="px-4 py-2">
                  <div className="font-medium">{e.employee_name}</div>
                  <div className="text-[11px] text-brand-grey">
                    {e.designation ?? "—"}{e.company ? ` · ${e.company}` : ""}
                    {e.branch ? ` · ${e.branch}` : ""}
                  </div>
                  {e.faults.length > 0 && (
                    <div className="text-[11px] text-status-red" data-files-faults={e.name}>
                      {e.faults.map((f) => t(`efl.fault.${f}`)).join(" · ")}
                    </div>
                  )}
                </td>
                <td className="w-32 px-4 py-2 text-right tabular-nums">
                  <span className={e.completeness.score < 0.5 ? "text-status-red font-semibold"
                    : e.completeness.score < 0.8 ? "text-status-amber" : "text-status-green"}>
                    {Math.round(e.completeness.score * 100)}%
                  </span>
                  <div className="text-[11px] text-brand-grey">
                    {t("efl.heldOf", { held: e.completeness.held, of: e.completeness.of })}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {worst.length > shown.length && (
          <button onClick={() => setShowAll(true)} data-files-more
            className="w-full border-t border-brand-grey-light px-4 py-2 text-xs font-semibold text-brand-primary">
            {t("efl.showAll", { n: worst.length })}
          </button>
        )}
      </SectionCard>

      <SectionCard title={t("efl.masters")} aside={t("efl.mastersHint")}>
        <table className="w-full text-sm">
          <tbody>
            <tr className="border-t border-brand-grey-light first:border-t-0" data-files-master="departments">
              <td className="px-4 py-2 font-medium">{t("efl.departments")}</td>
              <td className="px-4 py-2 text-right tabular-nums">
                <span className={d.departments.cluttered ? "text-status-amber font-semibold" : ""}>
                  {t("efl.emptyOf", { n: d.departments.empty, of: d.departments.total })}
                </span>
                <div className="text-[11px] text-brand-grey">{t("efl.singleStaff", { n: d.departments.single })}</div>
              </td>
            </tr>
            <tr className="border-t border-brand-grey-light" data-files-master="designations">
              <td className="px-4 py-2 font-medium">{t("efl.designations")}</td>
              <td className="px-4 py-2 text-right tabular-nums">
                <span className={d.designations.cluttered ? "text-status-amber font-semibold" : ""}>
                  {t("efl.emptyOf", { n: d.designations.empty, of: d.designations.total })}
                </span>
              </td>
            </tr>
            <tr className="border-t border-brand-grey-light" data-files-master="branches">
              <td className="px-4 py-2 font-medium">{t("efl.branches")}</td>
              <td className="px-4 py-2 text-right tabular-nums">{d.branches}</td>
            </tr>
            <tr className="border-t border-brand-grey-light" data-files-master="groups">
              <td className="px-4 py-2 font-medium">{t("efl.groups")}</td>
              <td className="px-4 py-2 text-right tabular-nums">{d.groups}</td>
            </tr>
            <tr className="border-t border-brand-grey-light" data-files-master="calendar">
              <td className="px-4 py-2 font-medium">{t("efl.calendar")}</td>
              <td className="px-4 py-2 text-right tabular-nums">
                <span className={d.calendar.running_out || d.calendar.expired ? "text-status-red font-semibold" : ""}>
                  {d.calendar.ends ? t("efl.calendarTo", { date: d.calendar.ends }) : t("efl.noCalendar")}
                </span>
                <div className="text-[11px] text-brand-grey">
                  {t("efl.calendarLists", { n: d.calendar.lists })}
                  {d.calendar_by_company.without_next_year.length > 0
                    ? ` · ${t("efl.calendarMissing", { n: d.calendar_by_company.without_next_year.length })}`
                    : ""}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <p className="border-t border-brand-grey-light bg-[#F6F8FA] px-4 py-2 text-[11px] text-brand-grey">
          {t("efl.mastersNote")}
        </p>
      </SectionCard>

      {calendar.isError && <ErrorBox error={calendar.error} />}
      {tidy.isError && <ErrorBox error={tidy.error} />}
    </div>
  );
}

export { SECTION_ORDER };
